import { Injectable } from '@angular/core';
import { HashMap, TranslocoService } from '@ngneat/transloco';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { firstValueFrom, Subscription } from 'rxjs';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';

@Injectable({
  providedIn: 'root',
})
/**
 * Translation service helps in translating objects
 */
export class TranslationService {
  locale = 'en';
  /**
   * @constructor
   */
  constructor(
    private readonly translocoService: TranslocoService,
    private readonly translocoLocaleService: TranslocoLocaleService
  ) {}

  /**
   * Translates profile menu items asynchronously
   * @param {ProfileMenuItems} items
   */
  translateSideMenuItems(items: any) {
    for (const str in items) {
      if (str === 'menu') {
        continue;
      }
      if (Object.prototype.hasOwnProperty.call(items, str)) {
        this.translateString(items[str][0]).then((s) => {
          items[str][0] = s;
        });
      }
    }
    this.translocoService
      .selectTranslate(items.menu)
      .subscribe((translation) => {
        items.menu = translation;
      });
    this.translocoService
      .selectTranslate(items.home[0])
      .subscribe((translation) => {
        items.home[0] = translation;
      });
    this.translocoService
      .selectTranslate(items.businessList[0])
      .subscribe((translation) => {
        items.businessList[0] = translation;
      });
  }

  /**
   * Translates object as if values where translatable keys
   * @param {any} obj to be translated
   * @param {Object<string, HashMap>?} params
   * @return {Subscription}
   */
  translateObject$(
    obj: any,
    params?: { [key: string]: HashMap }
  ): Subscription {
    const subsink = new Subscription();
    for (const str in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, str)) {
        let param: HashMap | undefined;

        if (params && Object.prototype.hasOwnProperty.call(params, str)) {
          param = params[str];
        }

        subsink.add(
          this.translocoService
            .selectTranslate(obj[str], param)
            .subscribe((translation) => {
              obj[str] = translation;
            })
        );
      }
    }
    return subsink;
  }

  /**
   * Translates object as if values where translatable keys
   * @param {any} obj to be translated
   * @param {Object<string, HashMap>?} params
   */
  async translateObject(obj: any, params?: { [key: string]: HashMap }) {
    for (const str in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, str)) {
        let param: HashMap | undefined;

        if (params && Object.prototype.hasOwnProperty.call(params, str)) {
          param = params[str];
        }

        obj[str] = await this.translateString(obj[str], param);
      }
    }
  }

  /**
   * Translates a string as if value where translatable
   * @param {string} str
   * @param {HashMap?} params
   * @return {Promise<string>}
   */
  async translateString(str: string, params?: HashMap): Promise<string> {
    return firstValueFrom(this.translocoService.selectTranslate(str, params));
  }

  /**
   * @param {number} n
   * @return {string}
   */
  localizeDecimal(n: number) {
    return this.translocoLocaleService.localizeNumber(n, 'decimal');
  }

  /**
   * @param {string} translocoLang
   * @return {TranslocoService}
   */
  setActiveLang(translocoLang?: string) {
    if (translocoLang) {
      return this.translocoService.setActiveLang(translocoLang);
    } else {
      return this.translocoService.setActiveLang(this.locale);
    }
  }

  /**
   * Translates a chip content
   * @param {string} chipContents
   */
  async translateChipContentArray(chipContents: ChipContent[]) {
    for (const chipContent of chipContents) {
      this.translateString(chipContent.value as string).then((translated) => {
        chipContent.value = translated;
      });
      if (chipContent.options) {
        this.translateChipContentArrayOptions(chipContent.options);
      }
    }
  }

  /**
   * Translates a set of options from chip content
   * @param {ChipContentOption[]} options
   */
  async translateChipContentArrayOptions(options: ChipContentOption[]) {
    for (const option of options) {
      this.translateString(option.text).then((translated) => {
        option.text = translated;
      });
    }
  }

  /**
   * @param {string | Date} date
   * @return {string}
   */
  localizeDate(date: string | Date): string {
    return this.translocoLocaleService.localizeDate(date).replace(/\//g, '.');
  }

  /**
   * @return {string}
   */
  getCurrencySymbol() {
    return this.translocoLocaleService.getCurrencySymbol();
  }

  /**
   *
   * @return {'en' | 'es' | 'pt-BR'}
   */
  getActiveLang(): 'en' | 'es' | 'pt-BR' {
    const activeLang = this.translocoService.getActiveLang();
    if (!['en', 'es', 'pt-BR'].includes(activeLang)) {
      throw new Error(`unknown language is active: ${activeLang}`);
    }
    return activeLang as any;
  }

  /**
   * @param {string} s
   * @param {number} n
   * @return {Promise<string>}
   */
  async translatePlural(s: string, n: number): Promise<string> {
    let sn = '';
    if (n === 0 || n === 1) {
      sn = `${s}-${n}`;
    } else {
      sn = `${s}-other`;
    }
    const translation: string = await firstValueFrom(
      this.translocoService.selectTranslate(sn)
    );
    return translation.replace(/<OTHER>/g, n.toString());
  }
}
